import React, { useEffect, useState } from "react";
import { Download, X, Smartphone, Apple } from "lucide-react";
import { isStandalone } from "../lib/pwa";

/**
 * Banner "Instalar app RAF Direct" — aparece só em mobile, uma vez por sessão,
 * quando o navegador dispara `beforeinstallprompt` (Android/Chrome/Edge).
 *
 * Para iOS Safari (não dispara o evento nativo) mostramos uma variante manual
 * com instruções "Compartilhar → Adicionar à tela inicial".
 */
const LS_DISMISS = "raf.pwa.dismissed_at";
const REPROMPT_DAYS = 7;

const InstallAppPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);
  const [expandedIos, setExpandedIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    // Não reincomodar por REPROMPT_DAYS
    try {
      const dismissed = localStorage.getItem(LS_DISMISS);
      if (dismissed) {
        const days = (Date.now() - Number(dismissed)) / 86400000;
        if (days < REPROMPT_DAYS) return;
      }
    } catch { /* ignore */ }

    const ua = navigator.userAgent || "";
    const isIosSafari = /iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
    if (isIosSafari) {
      setIos(true);
      setVisible(true);
      return;
    }
    if (window.__rafInstallEvent) {
      setVisible(true);
      return;
    }
    const on = () => setVisible(true);
    window.addEventListener("raf-installable", on);
    return () => window.removeEventListener("raf-installable", on);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(LS_DISMISS, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    const ev = window.__rafInstallEvent;
    if (!ev) return dismiss();
    ev.prompt();
    try { await ev.userChoice; } catch { /* ignore */ }
    window.__rafInstallEvent = null;
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] md:hidden animate-in slide-in-from-bottom duration-300"
      data-testid="pwa-install-prompt"
    >
      <div className="mx-3 mb-3 rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="p-4 flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-[#0B2B62] flex items-center justify-center">
            {ios ? <Apple className="w-6 h-6 text-white" /> : <Smartphone className="w-6 h-6 text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-[#0B2B62] text-sm">
              {ios ? "Adicionar RAF Direct ao iPhone" : "Instalar app RAF Direct"}
            </div>
            <p className="text-xs text-slate-500 mt-0.5 leading-snug">
              {ios
                ? "Um toque no botão Compartilhar → \"Adicionar à Tela de Início\"."
                : "Ícone na tela inicial, notificações e acesso offline. Instala em 2 seg."}
            </p>
          </div>
          <button onClick={dismiss} aria-label="Fechar" data-testid="pwa-dismiss"
            className="text-slate-400 hover:text-slate-700 p-1 -mr-1 -mt-1">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 pb-4 flex gap-2">
          {ios ? (
            <button onClick={() => setExpandedIos((v) => !v)}
              data-testid="pwa-ios-instructions"
              className="flex-1 text-xs bg-[#0B2B62] hover:bg-[#0a2454] text-white font-bold px-4 py-2.5 rounded-lg">
              {expandedIos ? "Ocultar instruções" : "Como fazer no iPhone"}
            </button>
          ) : (
            <button onClick={install} data-testid="pwa-install"
              className="flex-1 text-xs bg-emerald-700 hover:bg-emerald-800 text-white font-bold px-4 py-2.5 rounded-lg inline-flex items-center justify-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Instalar
            </button>
          )}
          <button onClick={dismiss} data-testid="pwa-later"
            className="text-xs border border-slate-300 text-slate-600 px-3 py-2.5 rounded-lg">
            Depois
          </button>
        </div>
        {ios && expandedIos && (
          <div className="px-4 pb-4 pt-2 border-t border-slate-100 text-[11px] text-slate-600 leading-relaxed space-y-1.5">
            <div><b>1.</b> Toque no ícone <b>Compartilhar</b> (quadrado com seta pra cima) na barra inferior do Safari.</div>
            <div><b>2.</b> Role para baixo e toque em <b>"Adicionar à Tela de Início"</b>.</div>
            <div><b>3.</b> Confirme em <b>"Adicionar"</b> no canto superior direito.</div>
            <div className="text-slate-500 pt-1">Pronto — o ícone RAF Direct aparece na sua tela e abre em tela cheia.</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallAppPrompt;
